import React, { useCallback, useEffect, useRef, useState } from "react"
import { useNavigationGuard } from "./zoneUploads/useNavigationGuard"
import { useAppContext } from "@ciq/shared/hooks/context"
import { Button } from "@ciq/shared/components/buttons"
import { EntryModal } from "@ciq/shared/components/modal"
import { ErrorToast } from "@ciq/shared/utils/toast"
import { InlineIndicator } from "@ciq/shared/components/loader"
import { ReactComponent as AlertIcon } from "@ciq/shared/assets/icons/alert_icon.svg"
import { getActiveJob, ProcessingSummary, useJobPolling, ZoneUploadManager, DataPreviewTable, approveJob, SuccessBanner, useZonePreviewData, zonesToCSV, generateCSVFilename, downloadCSV } from "./zoneUploads"
import { cancelJob } from "./zoneUploads/api"
import Divider from "@ciq/shared/components/divider"
import { useAnalyticsContext } from "@ciq/shared/contexts/AnalyticsContext"
import { FeatureFlags } from "@ciq/shared/constants/featureFlags"
import { UploadHistoryTable } from "./zoneUploads/UploadHistoryTable"

/**
 * Confirmation modal for canceling a pending upload job.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.show - Whether to show the modal
 * @param {Function} props.onHide - Callback when modal is closed
 * @param {Function} props.onConfirm - Callback when user confirms cancellation
 * @returns {React.ReactElement} The modal component
 */
function ConfirmCancelModal ({ show, onHide, onConfirm }) {
  const buttons = {
    secondary: {
      title: "Keep Upload",
      onClick: onHide
    },
    danger: {
      title: "Cancel Upload",
      onClick: onConfirm
    }
  }

  return (
    <EntryModal show={show} title="Cancel Incomplete Upload?" onHide={onHide} buttons={buttons}>
      <p>This will permanently cancel the incomplete upload. You can start a new upload after
        canceling.</p>
    </EntryModal>
  )
}

/**
 * Confirmation modal for discarding preview data.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.show - Whether to show the modal
 * @param {Function} props.onHide - Callback when modal is closed
 * @param {Function} props.onConfirm - Callback when user confirms discard
 * @returns {React.ReactElement} The modal component
 */
function ConfirmDiscardModal ({ show, onHide, onConfirm }) {
  const buttons = {
    secondary: {
      title: "Keep Reviewing",
      onClick: onHide
    },
    danger: {
      title: "Discard Data",
      onClick: onConfirm
    }
  }

  return (
    <EntryModal show={show} title="Discard Preview Data?" onHide={onHide} buttons={buttons}>
      <p>This will discard the processed zone data. You will need to upload and process the files again.</p>
    </EntryModal>
  )
}

/**
 * @typedef {Object} DataUploadsTabProps
 * @property {boolean} show - Boolean indicating if the tab is visible
 * @property {boolean} canManageSchool - Boolean indicating if user has school management permissions
 */

/**
 * Data Uploads tab for the School page.
 * Manages zone data upload workflow.
 * Fetches active job on mount to show appropriate view.
 *
 * @param {DataUploadsTabProps} props - Component props
 * @returns {React.ReactElement|null} The tab component or null if not shown
 */
export function DataUploadsTab ({ show, canManageSchool }) {
  const { schoolId, school, addLoadingId, removeLoadingId } = useAppContext()
  const { isFeatureEnabled } = useAnalyticsContext()
  const showUploadHistory = isFeatureEnabled(FeatureFlags.upload_history)

  // Stable refs for loading ID functions to avoid dependency array churn
  const addLoadingIdRef = useRef(addLoadingId)
  const removeLoadingIdRef = useRef(removeLoadingId)
  addLoadingIdRef.current = addLoadingId
  removeLoadingIdRef.current = removeLoadingId
  const [viewState, setViewState] = useState("loading") // loading | upload | pending | processing | results | success | error
  const [activeJobId, setActiveJobId] = useState(null)
  const [activeJobStatus, setActiveJobStatus] = useState(null)
  const [error, setError] = useState(null)
  const [isCancelling, setIsCancelling] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [showDiscardModal, setShowDiscardModal] = useState(false)
  const [summaryData, setSummaryData] = useState(null)
  const [isCommitting, setIsCommitting] = useState(false)
  const [commitJobId, setCommitJobId] = useState(null)
  const [previewJobId, setPreviewJobId] = useState(null)
  const [commitTimestamp, setCommitTimestamp] = useState(null)
  const historyRefetchRef = useRef(null)
  const [activeJobRefreshToken, setActiveJobRefreshToken] = useState(0)

  // Callback for UploadHistoryTable to refresh active job state after rollback
  const handleRollbackComplete = useCallback(() => {
    setActiveJobRefreshToken(t => t + 1)
    historyRefetchRef.current?.()
  }, [])

  // Polling hook - active during processing or when commit job is running
  const shouldPoll = viewState === "processing" || (commitJobId && viewState === "results")
  const { job: polledJob, isPolling, error: pollingError } = useJobPolling(
    schoolId,
    shouldPoll ? activeJobId : null
  )

  // Block browser refresh/close only during active processing or preview review
  // Do NOT block on: upload, loading, success, error, pending
  const shouldBlockNavigation = viewState === "processing" || viewState === "results"
  useNavigationGuard(shouldBlockNavigation)

  const zonePreviewData = useZonePreviewData(schoolId, previewJobId, { initialPageSize: 10 })

  // Fetch active job on mount
  useEffect(() => {
    if (!show || !canManageSchool || !schoolId) {
      return
    }

    // Synchronously reset state to prevent cross-school data flash
    setViewState("loading")
    setActiveJobId(null)
    setActiveJobStatus(null)
    setSummaryData(null)
    setPreviewJobId(null)
    setError(null)

    let mounted = true

    async function fetchActiveJob () {
      try {
        const activeJob = await getActiveJob(schoolId)

        if (!mounted) {
          return
        }

        if (activeJob) {
          setActiveJobId(activeJob.job_id)
          setActiveJobStatus(activeJob.status)

          // Determine view based on status
          if (activeJob.status === "PENDING") {
            // Job created but not submitted - show pending view to cancel or resume
            setViewState("pending")
          } else if (["SUBMITTED", "IN_PROGRESS", "CANCELLING"].includes(activeJob.status)) {
            setViewState("processing")
          } else {
            // Terminal states (COMPLETED, CANCELLED, ERROR) - allow new uploads
            setViewState("upload")
          }
        } else {
          setViewState("upload")
        }
      } catch (err) {
        if (!mounted) {
          return
        }
        console.error("Failed to fetch active job:", err)
        setError("Failed to load upload status. Please refresh the page.")
        setViewState("error")
      }
    }

    fetchActiveJob()

    return () => {
      mounted = false
    }
  }, [show, canManageSchool, schoolId, activeJobRefreshToken])

  // Handle job completion from polling
  useEffect(() => {
    if (!polledJob) {
      return
    }

    // Update status
    setActiveJobStatus(polledJob.status)

    // Determine if this is a COMMIT job (we started it via approve)
    const isCommitJob = commitJobId && activeJobId === commitJobId

    if (isCommitJob) {
      // Handle COMMIT job completion - all terminal states remove loading ID
      if (polledJob.status === "COMPLETED") {
        removeLoadingIdRef.current("zone_data_commit")
        setCommitTimestamp(polledJob.completed_at)
        historyRefetchRef.current?.()
        setViewState("success")
      } else if (polledJob.status === "ERROR") {
        removeLoadingIdRef.current("zone_data_commit")
        const details = polledJob.error_details
        const errorMessage = details?.message || "Failed to save zone data. Please try again."
        setError(errorMessage)
        historyRefetchRef.current?.()
        setViewState("error")
      } else if (polledJob.status === "CANCELLED") {
        removeLoadingIdRef.current("zone_data_commit")
        setCommitJobId(null)
        setActiveJobId(null)
        setActiveJobStatus(null)
        historyRefetchRef.current?.()
        setViewState("upload")
      }
    } else {
      // Handle PREVIEW job completion - use results directly from backend
      if (polledJob.status === "COMPLETED" && polledJob.results) {
        setSummaryData(polledJob.results)
        setPreviewJobId(activeJobId) // Preserve for DataPreviewTable
        setViewState("results")
      } else if (polledJob.status === "COMPLETED" && !polledJob.results) {
        // Job completed but no results available (e.g., S3 read failed)
        setViewState("error")
        setError("Processing completed but results are not available. Please try uploading again.")
      }

      // Handle error state
      if (polledJob.status === "ERROR") {
        setViewState("error")
        // Backend returns standardized error_details: { code, message }
        const details = polledJob.error_details
        const errorMessage = details?.message || "Processing failed. Please try again."
        setError(errorMessage)
        historyRefetchRef.current?.()
      }

      // Handle cancelled state
      if (polledJob.status === "CANCELLED") {
        setActiveJobId(null)
        setActiveJobStatus(null)
        historyRefetchRef.current?.()
        setViewState("upload")
      }
    }
  }, [polledJob, commitJobId, activeJobId])

  // Handle polling errors
  useEffect(() => {
    if (pollingError) {
      setError(pollingError)
      setViewState("error")
    }
  }, [pollingError])

  const handleProcessingStart = useCallback((jobId) => {
    setActiveJobId(jobId)
    setActiveJobStatus("SUBMITTED")
    setViewState("processing")
  }, [])

  const handleShowConfirmModal = useCallback(() => {
    setShowConfirmModal(true)
  }, [])

  const handleHideConfirmModal = useCallback(() => {
    setShowConfirmModal(false)
  }, [])

  const handleShowDiscardModal = useCallback(() => {
    setShowDiscardModal(true)
  }, [])

  const handleHideDiscardModal = useCallback(() => {
    setShowDiscardModal(false)
  }, [])

  const handleDiscardPreview = useCallback(() => {
    setShowDiscardModal(false)
    setSummaryData(null)
    setActiveJobId(null)
    setActiveJobStatus(null)
    setViewState("upload")
  }, [])

  const handleApprove = useCallback(async () => {
    setIsCommitting(true)
    setError(null)
    addLoadingIdRef.current("zone_data_commit")

    try {
      const { job_id } = await approveJob(schoolId, previewJobId)
      setCommitJobId(job_id)
      setActiveJobId(job_id)
      setActiveJobStatus("SUBMITTED")
      // Stay on results view - don't switch to processing UI
    } catch (err) {
      console.error("Failed to approve upload:", err)
      setCommitJobId(null)
      removeLoadingIdRef.current("zone_data_commit")
      ErrorToast(err.message || "Failed to start save operation. Please try again.")
    } finally {
      setIsCommitting(false)
    }
  }, [schoolId, previewJobId])

  const handleDownloadCSV = useCallback(() => {
    const filename = generateCSVFilename(school?.name)
    downloadCSV(zonesToCSV(zonePreviewData.allZones), filename)
  }, [zonePreviewData.allZones, school?.name])

  const handleUploadAnother = useCallback(() => {
    setSummaryData(null)
    setActiveJobId(null)
    setActiveJobStatus(null)
    setCommitJobId(null)
    setPreviewJobId(null)
    setCommitTimestamp(null)
    setError(null)
    setViewState("upload")
  }, [])

  const handleCancelPendingJob = useCallback(async () => {
    if (!activeJobId) {
      return
    }

    setIsCancelling(true)
    setShowConfirmModal(false)
    try {
      await cancelJob(schoolId, activeJobId)
      setActiveJobId(null)
      setActiveJobStatus(null)
      setViewState("upload")
    } catch (err) {
      console.error("Failed to cancel pending job:", err)
      ErrorToast("Failed to cancel the pending upload. Please try again.")
    } finally {
      setIsCancelling(false)
    }
  }, [schoolId, activeJobId])

  if (!show || !canManageSchool) {
    return null
  }

  /**
   * Renders the appropriate content based on the current view state.
   *
   * @returns {React.ReactElement} The view content for the current state
   */
  function renderViewContent () {
    switch (viewState) {
      case "loading":
        return (
          <div className="school-data-uploads-loading">
            Loading...
          </div>
        )

      case "error": {
        // Determine if this was a commit error (we have a commitJobId but error occurred)
        const isCommitError = commitJobId !== null

        return (
          <>
            <div className="zone-upload-manager__error">
              <span>{error}</span>
              <button type="button"
                className="zone-upload-manager__error-dismiss"
                onClick={() => {
                  setError(null)
                  setActiveJobId(null)
                  setActiveJobStatus(null)
                  setCommitJobId(null)
                  setSummaryData(null)
                  setViewState("upload")
                }}
                aria-label="Dismiss error"
              >
                &times;
              </button>
            </div>
            {isCommitError ? (
              <>
                {/* Preserve preview data on commit error so users don't lose context */}
                {summaryData && (
                  <>
                    <ProcessingSummary data={summaryData}/>
                    <Divider/>
                    <DataPreviewTable
                      data={zonePreviewData.data}
                      totalCount={zonePreviewData.totalCount}
                      page={zonePreviewData.page}
                      pageSize={zonePreviewData.pageSize}
                      searchQuery={zonePreviewData.searchQuery}
                      isLoading={zonePreviewData.isLoading}
                      error={zonePreviewData.error}
                      setPage={zonePreviewData.setPage}
                      setPageSize={zonePreviewData.setPageSize}
                      setSearchQuery={zonePreviewData.setSearchQuery}
                      retry={zonePreviewData.retry}
                    />
                    <Divider/>
                  </>
                )}
                <div className="school-data-uploads-actions">
                  <Button
                    variant="secondary"
                    onClick={handleUploadAnother}
                  >
                    Start Over
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => {
                      setError(null)
                      setCommitJobId(null)
                      handleApprove()
                    }}
                  >
                    Try Again
                  </Button>
                </div>
              </>
            ) : (
              <ZoneUploadManager
                onProcessingStart={handleProcessingStart}
              />
            )}
          </>
        )
      }

      case "pending":
        return (
          <div className="school-data-uploads-pending">
            <div className="school-data-uploads-pending__icon">
              <AlertIcon className="red-color"/>
            </div>
            <h3 className="school-data-uploads-pending__title">Incomplete Upload Found</h3>
            <p className="school-data-uploads-pending__message">
              A previous upload was started but not completed.
              Cancel it to start a new upload.
            </p>
            <Button
              variant="danger"
              onClick={handleShowConfirmModal}
              disabled={isCancelling}
            >
              {isCancelling ? "Cancelling..." : "Cancel Previous Upload"}
            </Button>
          </div>
        )

      case "processing":
        return (
          <div className="school-data-uploads-processing" role="status" aria-live="polite">
            <InlineIndicator show={true}/>
            <p className="school-data-uploads-processing__message">
              Processing files... This may take a few moments.
            </p>
            {activeJobStatus && (
              <p className="school-data-uploads-processing__status">
                Status: {activeJobStatus}
              </p>
            )}
          </div>
        )

      case "results":
        if (!summaryData) {
          return null
        }
        return (
          <>
            <ProcessingSummary data={summaryData}/>
            <Divider/>
            <DataPreviewTable
              data={zonePreviewData.data}
              totalCount={zonePreviewData.totalCount}
              page={zonePreviewData.page}
              pageSize={zonePreviewData.pageSize}
              searchQuery={zonePreviewData.searchQuery}
              isLoading={zonePreviewData.isLoading}
              error={zonePreviewData.error}
              setPage={zonePreviewData.setPage}
              setPageSize={zonePreviewData.setPageSize}
              setSearchQuery={zonePreviewData.setSearchQuery}
              retry={zonePreviewData.retry}
            />
            <Divider/>
            <div className="school-data-uploads-actions">
              <Button
                variant="secondary"
                onClick={handleShowDiscardModal}
                disabled={isCommitting || commitJobId}
              >
                Cancel
              </Button>
              <div className="school-data-uploads-actions__right">
                <Button
                  variant="secondary"
                  onClick={handleDownloadCSV}
                  disabled={isCommitting || commitJobId || zonePreviewData.isLoading || zonePreviewData.allZones.length === 0}
                >
                  Download CSV
                </Button>
                <Button
                  variant="primary"
                  onClick={handleApprove}
                  disabled={isCommitting || commitJobId}
                >
                  {isCommitting || commitJobId ? "Saving..." : "Approve & Upload"}
                </Button>
              </div>
            </div>
          </>
        )

      case "success":
        if (!commitTimestamp) {
          return null
        }
        return (
          <SuccessBanner
            timestamp={commitTimestamp}
            onUploadAnother={handleUploadAnother}
          />
        )

      default:
        return (
          <ZoneUploadManager
            onProcessingStart={handleProcessingStart}
          />
        )
    }
  }

  return (
    <div className="school-data-uploads-tab">
      {renderViewContent()}

      {showUploadHistory && viewState !== "loading" && (
        <>
          <Divider/>
          <UploadHistoryTable schoolId={schoolId} schoolName={school?.name} refetchRef={historyRefetchRef} onRollbackComplete={handleRollbackComplete} />
        </>
      )}

      <ConfirmCancelModal
        show={showConfirmModal}
        onHide={handleHideConfirmModal}
        onConfirm={handleCancelPendingJob}
      />
      <ConfirmDiscardModal
        show={showDiscardModal}
        onHide={handleHideDiscardModal}
        onConfirm={handleDiscardPreview}
      />
    </div>
  )
}
